import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Modal, ScrollView, StyleSheet } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { ThemeType } from '../contexts/ThemeContext';

interface ThemeSwitcherProps {
  visible: boolean;
  onClose: () => void;
}

export const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({ visible, onClose }) => {
  const { currentTheme, themeData, setTheme, availableThemes } = useTheme();
  const [selectedTheme, setSelectedTheme] = useState<ThemeType>(currentTheme);

  const handleThemeSelect = (theme: ThemeType) => {
    setSelectedTheme(theme);
  };

  const handleApplyTheme = () => {
    setTheme(selectedTheme);
    onClose();
  };

  const themes = {
    tiki: { name: 'Tiki Paradise', icon: '🌺', description: 'Tropical vibes with bamboo and palm fronds' },
    pirate: { name: 'Pirate Cove', icon: '🏴‍☠️', description: 'Swashbuckling adventure with rum and treasure' },
    british: { name: 'British Gentleman', icon: '🇬🇧', description: 'Sophisticated gin and afternoon tea' },
    classic: { name: 'Classic Cocktail', icon: '🍸', description: 'Timeless elegance and sophistication' },
    tropical: { name: 'Tropical Sunset', icon: '🌅', description: 'Vibrant colors and island vibes' },
    speakeasy: { name: 'Speakeasy', icon: '🎭', description: '1920s prohibition era glamour' }
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={[styles.overlay, { backgroundColor: themeData.colors.background + 'CC' }]}>
        <View style={[styles.container, { backgroundColor: themeData.colors.surface }]}>
          <View style={[styles.header, { borderBottomColor: themeData.colors.border }]}>
            <Text style={[styles.title, { color: themeData.colors.text }]}>
              Choose Your Theme
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={[styles.closeText, { color: themeData.colors.textSecondary }]}>✕</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.themeList} showsVerticalScrollIndicator={false}>
            {availableThemes.map((theme) => {
              const themeInfo = themes[theme];
              const isSelected = selectedTheme === theme;
              
              return (
                <TouchableOpacity
                  key={theme}
                  style={[
                    styles.themeOption,
                    { 
                      backgroundColor: isSelected ? themeData.colors.primary : themeData.colors.surface,
                      borderColor: isSelected ? themeData.colors.primary : themeData.colors.border
                    }
                  ]}
                  onPress={() => handleThemeSelect(theme)}
                >
                  <View style={styles.themeIcon}>
                    <Text style={styles.iconText}>{themeInfo.icon}</Text>
                  </View>
                  <View style={styles.themeInfo}>
                    <Text style={[
                      styles.themeName,
                      { color: isSelected ? '#FFFFFF' : themeData.colors.text }
                    ]}>
                      {themeInfo.name}
                    </Text>
                    <Text style={[
                      styles.themeDescription,
                      { color: isSelected ? '#FFFFFFCC' : themeData.colors.textSecondary }
                    ]}>
                      {themeInfo.description}
                    </Text>
                  </View>
                  {isSelected && (
                    <View style={styles.selectedIndicator}>
                      <Text style={styles.checkmark}>✓</Text>
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </ScrollView>

          <View style={[styles.footer, { borderTopColor: themeData.colors.border }]}>
            <TouchableOpacity
              style={[styles.cancelButton, { borderColor: themeData.colors.border }]}
              onPress={onClose}
            >
              <Text style={[styles.cancelText, { color: themeData.colors.textSecondary }]}>
                Cancel
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.applyButton, { backgroundColor: themeData.colors.primary }]}
              onPress={handleApplyTheme}
            >
              <Text style={[styles.applyText, { color: '#FFFFFF' }]}>
                Apply Theme
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  container: {
    width: '100%',
    maxWidth: 400,
    maxHeight: '80%',
    borderRadius: 16,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  closeButton: {
    padding: 8,
  },
  closeText: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  themeList: {
    flex: 1,
    padding: 16,
  },
  themeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    marginBottom: 12,
    borderRadius: 12,
    borderWidth: 2,
  },
  themeIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  iconText: {
    fontSize: 24,
  },
  themeInfo: {
    flex: 1,
  },
  themeName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  themeDescription: {
    fontSize: 14,
    lineHeight: 20,
  },
  selectedIndicator: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkmark: {
    color: '#000000',
    fontSize: 16,
    fontWeight: 'bold',
  },
  footer: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  cancelText: {
    fontSize: 16,
    fontWeight: '600',
  },
  applyButton: {
    flex: 1,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  applyText: {
    fontSize: 16,
    fontWeight: '600',
  },
});
